<template>
  <div class="loan-approval-table-container">
    <div class="d-flex justify-space-between align-center p-4 bg-white border-b">
      <span class="text-lg font-semibold">Loan Closure Approvals</span>
      <div class="d-flex align-center">
        <v-select
          v-model="filters.status"
          label="Select Status"
          :items="statusOptions"
          solo
          flat
          clearable
          outlined
          dense
          hide-details
          class="mr-2"
          style="width: 200px"
          @change="fetchApprovals"
        ></v-select>
        <v-btn color="primary" icon @click="fetchApprovals" :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="approvals"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalItems"
      class="table-radius bg-white"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
    >
      <!-- Custom headers with tooltips if needed -->
      <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
        <p :key="`header-${index}`" class="font-title mb-0">{{ header.text }}</p>
      </template>

      <!-- Info Action column -->
      <template v-slot:[`item.viewDetails`]="{ item }">
        <v-btn icon small color="primary" @click="openInfoDialog(item)">
          <v-icon small>mdi-information-outline</v-icon>
        </v-btn>
      </template>

      <!-- Loan Application column -->
      <template v-slot:[`item.loanDetails`]="{ item }">
        <div class="p-2">
          <div
            class="loan-app-link d-inline-flex align-center font-semibold text-blue-600 mb-0 cursor-pointer"
            @click.stop="navigateToLoan(item)"
            :title="'Click to view case in workflow'"
          >
            <span>
              {{
                item.loanDetails?.applicationNumber ||
                item.snapshot?.loanDetails?.applicationNumber ||
                item.payload?.applicationNumber ||
                item.payload?.caseNo ||
                item.caseDetails?.caseNo ||
                'N/A'
              }}
            </span>
            <v-icon x-small color="primary" class="ml-1 link-icon">mdi-open-in-new</v-icon>
          </div>
          <div>
            <span class="text-xs text-gray-500">
              {{ getBorrowerName(item) }}
            </span>
          </div>
        </div>
      </template>

      <!-- Loan Amount column -->
      <template v-slot:[`item.loanAmount`]="{ item }">
        <span class="font-semibold text-gray-800">
          ₹{{ formatCurrency(getLoanAmount(item)) }}
        </span>
      </template>

      <!-- Requested By & Date column -->
      <template v-slot:[`item.requestedBy`]="{ item }">
        <div class="p-2">
          <p class="font-semibold mb-0">
            {{ getRequesterName(item) }}
          </p>
          <span class="text-xs text-gray-500">
            {{ formatDate(item.requestedOn) }}
          </span>
        </div>
      </template>

      <!-- Type column -->
      <template v-slot:[`item.approvalType`]="{ item }">
        <v-chip
          small
          label
          class="font-semibold"
          :color="
            item.approvalType === 'FORECLOSURE'
              ? 'red lighten-5 text-red-700'
              : 'blue lighten-5 text-blue-700'
          "
        >
          {{ item.approvalType }}
        </v-chip>
      </template>

      <!-- Amount column -->
      <template v-slot:[`item.amount`]="{ item }">
        <span class="font-semibold text-green-700">
          ₹{{ formatCurrency(item.payload?.totalAmtPaid) }}
        </span>
      </template>

      <!-- Payment Remarks column -->
      <template v-slot:[`item.remarks`]="{ item }">
        <div class="max-w-xs truncate-remarks">
          <span v-if="item.payload?.remarks" class="text-sm font-medium">
            {{ item.payload.remarks }}
          </span>
          <span v-else class="text-xs text-gray-400 italic">No remarks</span>
        </div>
      </template>

      <!-- Status column -->
      <template v-slot:[`item.status`]="{ item }">
        <v-chip small class="font-semibold" :color="getStatusColor(item.status)" dark>
          {{ item.status }}
        </v-chip>
      </template>

      <!-- Action column -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center gap-2" v-if="canUserApprove(item)">
          <v-btn
            small
            color="success"
            class="text-none font-bold mr-2"
            @click="openActionDialog(item, 'APPROVE')"
          >
            Approve
          </v-btn>
          <v-btn
            small
            color="error"
            outlined
            class="text-none font-bold"
            @click="openActionDialog(item, 'REJECT')"
          >
            Reject
          </v-btn>
        </div>
        <div v-else class="text-xs text-gray-500">
          <div v-if="item.status === 'PENDING'">
            <span class="font-semibold text-amber-600">
              {{ getPendingApproversList(item) }}
            </span>
          </div>
          <div v-else-if="item.approvers && item.approvers.length">
            <span class="font-semibold">By:</span>
            {{ getApproverName(item) }}
            <div v-if="getApproverRemarks(item)" class="italic mt-1 text-gray-600">
              "{{ getApproverRemarks(item) }}"
            </div>
          </div>
          <span v-else>-</span>
        </div>
      </template>
    </v-data-table>

    <!-- Dialog for Approve/Reject remarks/reason -->
    <v-dialog v-model="dialog.show" max-width="500px" persistent>
      <v-card class="p-2">
        <v-card-title class="text-h6 font-bold pb-2 border-b">
          {{ dialog.actionType === 'APPROVE' ? 'Approve Loan Closure' : 'Reject Loan Closure' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-sm my-4 text-gray-600">
            Are you sure you want to
            {{ dialog.actionType === 'APPROVE' ? 'approve' : 'reject' }} the closure request for
            loan
            <strong class="text-black">{{
              dialog.item?.loanDetails?.applicationNumber ||
              dialog.item?.snapshot?.loanDetails?.applicationNumber
            }}</strong
            >?
          </p>

          <v-textarea
            v-model="dialog.remarks"
            outlined
            dense
            :label="dialog.actionType === 'APPROVE' ? 'Approval Remarks' : 'Rejection Reason'"
            rows="3"
            placeholder="Enter comment here..."
            hide-details
            class="mt-2"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="p-4 border-t d-flex justify-end gap-2 bg-gray-50">
          <v-btn @click="closeActionDialog" outlined class="text-none rounded-md px-4"
            >Cancel</v-btn
          >
          <v-btn
            :color="dialog.actionType === 'APPROVE' ? 'success' : 'error'"
            class="text-none font-bold rounded-md px-4 white--text"
            :loading="dialog.submitting"
            @click="submitApprovalAction"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for detailed read-only request view -->
    <v-dialog v-model="infoDialog.show" max-width="850px">
      <v-card tile class="px-2">
        <v-card-title
          class="text-h6 font-bold pb-4 border-b d-flex justify-space-between align-center px-0"
        >
          <span>Loan Closure Request Details</span>
          <v-btn icon @click="infoDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-6 overflow-y-auto px-0" style="max-height: 70vh">
          <div class="row">
            <!-- Left Column: Borrower & Loan Details -->
            <div class="col-md-6 border-r pa-4">
              <h3 class="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider font-title">
                Account Information
              </h3>
              <div class="mb-4">
                <div class="d-flex justify-space-between py-2 border-b text-sm align-center">
                  <span class="text-gray-500">Application Number:</span>
                  <span
                    class="loan-app-link font-semibold text-blue-600 cursor-pointer d-inline-flex align-center"
                    @click="navigateToLoan(infoDialog.item)"
                    :title="'Click to view case in workflow'"
                  >
                    <span>{{
                      infoDialog.item?.loanDetails?.applicationNumber ||
                      infoDialog.item?.snapshot?.loanDetails?.applicationNumber ||
                      infoDialog.item?.payload?.applicationNumber ||
                      infoDialog.item?.payload?.caseNo ||
                      infoDialog.item?.caseDetails?.caseNo ||
                      'N/A'
                    }}</span>
                    <v-icon x-small color="primary" class="ml-1 link-icon">mdi-open-in-new</v-icon>
                  </span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Borrower Name:</span>
                  <span class="font-semibold text-black">{{
                    getBorrowerName(infoDialog.item)
                  }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Guarantor(s):</span>
                  <span class="font-semibold text-black">{{ getGuarantorsText() }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Amount Sanctioned:</span>
                  <span class="font-bold text-blue-800">₹{{
                    formatCurrency(getLoanAmount(infoDialog.item))
                  }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Closure Type:</span>
                  <span class="font-semibold text-blue-600 font-bold">{{
                    infoDialog.item?.approvalType
                  }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Requested By:</span>
                  <span class="font-semibold text-black">{{
                    getRequesterName(infoDialog.item)
                  }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Requested On:</span>
                  <span class="font-semibold text-black">{{
                    formatDate(infoDialog.item?.requestedOn)
                  }}</span>
                </div>
              </div>

              <h3
                class="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider font-title mt-4"
              >
                Payment Parameters
              </h3>
              <div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Payment Date:</span>
                  <span class="font-semibold text-black">{{
                    infoDialog.item?.payload?.paymentDate || 'N/A'
                  }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Payment Mode:</span>
                  <span class="font-semibold text-black">{{
                    infoDialog.item?.payload?.mode ||
                    infoDialog.item?.payload?.paymentMethod ||
                    'N/A'
                  }}</span>
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-500">Selected Ledger:</span>
                  <span class="font-semibold text-blue-700">{{
                    infoDialog.item?.payload?.ledgerName || 'N/A'
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Right Column: Financial Breakup -->
            <div class="col-md-6 px-4">
              <h3 class="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider font-title">
                Financial Breakdown
              </h3>
              <div class="bg-gray-50 p-3 rounded-0 border">
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Principal Outstanding:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(
                        infoDialog.item?.payload?.receivablesActuals?.principalOutstanding
                      )
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Interest Outstanding:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(
                        infoDialog.item?.payload?.receivablesActuals?.interestOutstanding
                      )
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Broken Interest:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(infoDialog.item?.payload?.receivablesActuals?.brokenInterest)
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Foreclosure Charges:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(
                        infoDialog.item?.payload?.receivablesActuals?.foreclosureCharges
                      )
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">LPC (Late Fee):</span>
                  <span class="font-semibold text-black"
                    >₹{{ formatCurrency(infoDialog.item?.payload?.receivablesActuals?.lpc) }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Handloan Amount:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(infoDialog.item?.payload?.receivablesActuals?.handloan)
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Handloan LPC:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(infoDialog.item?.payload?.receivablesActuals?.handloanLpc)
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">VAS Charges:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(infoDialog.item?.payload?.receivablesActuals?.vasCharge)
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">VAS Collect Later:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(infoDialog.item?.payload?.receivablesActuals?.vasCollectLater)
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Insurance Amount:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(infoDialog.item?.payload?.receivablesActuals?.insurance)
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm">
                  <span class="text-gray-600">Other Charges:</span>
                  <span class="font-semibold text-black"
                    >₹{{
                      formatCurrency(
                        infoDialog.item?.payload?.receivablesActuals?.['Other Charges'] ||
                          infoDialog.item?.payload?.receivablesActuals?.otherCharges
                      )
                    }}</span
                  >
                </div>
                <div class="d-flex justify-space-between py-2 border-b text-sm text-red-600">
                  <span class="font-medium">Discount / Waiver:</span>
                  <span class="font-semibold"
                    >- ₹{{ formatCurrency(infoDialog.item?.payload?.discount) }}</span
                  >
                </div>

                <div
                  class="d-flex justify-space-between pt-3 mt-2 border-t text-base font-bold text-green-700 bg-white p-2"
                >
                  <span>Total Paid Amount:</span>
                  <span>₹{{ formatCurrency(infoDialog.item?.payload?.totalAmtPaid) }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pt-4 border-t d-flex justify-end px-0">
          <v-btn @click="infoDialog.show = false" color="primary" class="text-none px-4" tile
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'LoanApprovalTable',
  data() {
    return {
      approvals: [],
      loading: false,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      filters: {
        status: ''
      },
      headers: [
        { text: 'Info', value: 'viewDetails', sortable: false },
        { text: 'Loan Application', value: 'loanDetails', sortable: false },
        { text: 'Amount Sanctioned', value: 'loanAmount', sortable: false },
        { text: 'Requested By', value: 'requestedBy', sortable: false },
        { text: 'Type', value: 'approvalType', sortable: false },
        { text: 'Paid Amount', value: 'amount', sortable: false },
        { text: 'Payment Remarks', value: 'remarks', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Action / Reviewer Comments', value: 'actions', sortable: false }
      ],
      statusOptions: [
        { text: 'All', value: '' },
        { text: 'Pending', value: 'PENDING' },
        { text: 'Approved', value: 'APPROVED' },
        { text: 'Rejected', value: 'REJECTED' }
      ],
      dialog: {
        show: false,
        actionType: 'APPROVE', // 'APPROVE' or 'REJECT'
        item: null,
        remarks: '',
        submitting: false
      },
      infoDialog: {
        show: false,
        item: null
      }
    }
  },
  mounted() {
    this.fetchApprovals()
  },
  watch: {
    options: {
      handler() {
        this.fetchApprovals()
      },
      deep: true
    }
  },
  methods: {
    async fetchApprovals() {
      this.loading = true
      try {
        const { page, itemsPerPage } = this.options
        const params = {
          page,
          limit: itemsPerPage,
          status: this.filters.status || undefined
        }
        const response = await this.$axios.get('/collection/loan-approvals', { params })
        this.approvals = response.data?.approvals || []
        this.totalItems = response.data?.pagination?.totalCount || 0
      } catch (error) {
        console.error('Error fetching loan approvals:', error)
        this.$toast.error('Failed to load loan approvals.')
      } finally {
        this.loading = false
      }
    },
    getBorrowerName(item) {
      if (!item) return 'Borrower'
      const cust = item.customerDetails
      if (cust) {
        return (
          cust.customerFullName ||
          `${cust.firstName || ''} ${cust.lastName || ''}`.trim() ||
          item.payload?.borrowerName ||
          'Borrower'
        )
      }
      return item.payload?.borrowerName || 'Borrower'
    },
    getRequesterName(item) {
      if (!item) return 'Unknown'
      const u =
        item.requesterDetails || (typeof item.requestedBy === 'object' ? item.requestedBy : null)
      if (u) {
        return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name || 'User'
      }
      return 'Unknown'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return this.$dayjs(dateStr).format('DD/MM/YYYY hh:mm A')
    },
    formatCurrency(val) {
      if (val === undefined || val === null) return '0'
      return Number(val).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    getStatusColor(status) {
      switch (status) {
        case 'PENDING':
          return 'orange'
        case 'APPROVED':
          return 'success'
        case 'REJECTED':
          return 'error'
        default:
          return 'grey'
      }
    },
    openActionDialog(item, actionType) {
      this.dialog.item = item
      this.dialog.actionType = actionType
      this.dialog.remarks = ''
      this.dialog.show = true
    },
    closeActionDialog() {
      this.dialog.show = false
      this.dialog.item = null
      this.dialog.remarks = ''
      this.dialog.submitting = false
    },
    async submitApprovalAction() {
      if (!this.dialog.item) return
      this.dialog.submitting = true
      const id = this.dialog.item._id
      const url = `/collection/loan-approvals/${id}/${
        this.dialog.actionType === 'APPROVE' ? 'approve' : 'reject'
      }`

      const payload = {}
      if (this.dialog.actionType === 'APPROVE') {
        payload.remarks = this.dialog.remarks
      } else {
        payload.remarks = this.dialog.remarks
        payload.rejectionReason = this.dialog.remarks || 'Rejected by reviewer'
      }

      try {
        await this.$axios.post(url, payload)
        this.$toast.success(
          `Request ${this.dialog.actionType === 'APPROVE' ? 'approved' : 'rejected'} successfully.`
        )
        this.closeActionDialog()
        this.fetchApprovals()
      } catch (error) {
        console.error('Error processing approval action:', error)
        const msg = error?.response?.data?.message || 'Failed to update approval status.'
        this.$toast.error(msg)
        this.dialog.submitting = false
      }
    },
    getApproverName(item) {
      if (item && item.approvers && item.approvers.length) {
        // Find the approver who took action
        const actionApprover = item.approvers.find((a) => a.userAction !== 'PENDING')
        if (actionApprover) {
          const userId = actionApprover.userId?._id || actionApprover.userId
          if (item.approverUsers) {
            const u = item.approverUsers.find(
              (au) => (au._id || au).toString() === userId.toString()
            )
            if (u) {
              return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name
            }
          }
          return 'Reviewer'
        }
      }
      return 'Reviewer'
    },
    getApproverRemarks(item) {
      if (item && item.approvers && item.approvers.length) {
        const actionApprover = item.approvers.find((a) => a.userAction !== 'PENDING')
        return actionApprover?.remarks || ''
      }
      return ''
    },
    canUserApprove(item) {
      if (item.status !== 'PENDING') return false
      const loggedInUserId = this.$storage?.getUniversal('user')?._id
      if (!loggedInUserId) return false
      return item.approvers?.some(
        (a) =>
          (a.userId?._id || a.userId)?.toString() === loggedInUserId.toString() &&
          a.userAction === 'PENDING'
      )
    },
    getPendingApproversList(item) {
      if (!item || !item.approvers) return 'Pending'
      const pendingApprovers = item.approvers.filter((a) => a.userAction === 'PENDING')
      if (!pendingApprovers.length) return 'Pending'
      const names = pendingApprovers.map((pa) => {
        const userId = pa.userId?._id || pa.userId
        if (item.approverUsers) {
          const u = item.approverUsers.find((au) => (au._id || au).toString() === userId.toString())
          if (u) {
            return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name
          }
        }
        return 'Reviewer'
      })
      return `Pending (${names.join(', ')})`
    },
    openInfoDialog(item) {
      this.infoDialog.item = item
      this.infoDialog.show = true
    },
    getGuarantorsText() {
      const item = this.infoDialog.item
      if (item?.payload?.guarantorName && item.payload.guarantorName !== 'N/A') {
        return item.payload.guarantorName
      }
      return 'None'
    },
    getLoanAmount(item) {
      if (!item) return 0
      return (
        item.payload?.loanAmountSanctioned ||
        item.payload?.loanAmount ||
        item.loanDetails?.loanAmountSanctioned ||
        item.loanDetails?.loanAmountRequested ||
        item.loanDetails?.principal ||
        item.snapshot?.loanDetails?.loanAmountSanctioned ||
        item.snapshot?.loanDetails?.loanAmountRequested ||
        item.snapshot?.loanDetails?.principal ||
        0
      )
    },
    async navigateToLoan(item) {
      if (!item) return
      const appNo =
        item.loanDetails?.applicationNumber ||
        item.snapshot?.loanDetails?.applicationNumber ||
        item.payload?.applicationNumber ||
        item.payload?.caseNo ||
        item.caseDetails?.caseNo

      if (!appNo || appNo === 'N/A') {
        this.$toast.warning('No valid loan/case number found for navigation.')
        return
      }

      try {
        let useCase =
          item.caseDetails?.useCase ||
          item.payload?.useCase ||
          item.useCase ||
          item.loanDetails?.useCase ||
          ''
        let subCaseId =
          item.caseDetails?.subCase?._id ||
          item.caseDetails?.subCase ||
          item.payload?.subCase?._id ||
          item.payload?.subCase ||
          item.subCase ||
          item.loanDetails?.subCase

        // If subCaseId or useCase is missing, look up from store or orgSetup API
        let subCases = this.$store?.state?.subCases || []
        if (!subCases.length) {
          try {
            const res = await this.$axios.get('/orgSetup/extend-usecase')
            subCases = res.data?.data || []
            if (this.$store) {
              this.$store.dispatch('setSubCases', subCases)
            }
          } catch (e) {
            console.error('Error pre-fetching subCases in LoanApprovalTable:', e)
          }
        }

        // If useCase is resolved but subCaseId is not, find the first matching subcase
        if (useCase && !subCaseId) {
          const matched = subCases.find((sc) => sc.useCase === useCase)
          if (matched) {
            subCaseId = matched._id
          }
        }

        // If subCaseId is resolved but useCase is not, find useCase from subCases list
        if (subCaseId && !useCase) {
          const matched = subCases.find((sc) => (sc._id || sc).toString() === subCaseId.toString())
          if (matched) {
            useCase = matched.useCase
          }
        }

        // Fallback: Default to collection or loan_management, or the first available subcase
        if (!useCase || !subCaseId) {
          const collectionSubCase = subCases.find(
            (sc) => sc.useCase === 'collection' || sc.useCase === 'collection_management'
          )
          if (collectionSubCase) {
            useCase = collectionSubCase.useCase
            subCaseId = collectionSubCase._id
          } else if (subCases.length > 0) {
            useCase = subCases[0].useCase
            subCaseId = subCases[0]._id
          }
        }

        if (!useCase || !subCaseId) {
          this.$toast.error('Unable to resolve workspace route for this loan.')
          return
        }

        if (this.infoDialog?.show) {
          this.infoDialog.show = false
        }

        // Resolve route URL and open in a new tab
        const routeData = this.$router.resolve({
          path: `/workforce/${useCase}/${subCaseId}`,
          query: { caseNo: appNo }
        })
        window.open(routeData.href, '_blank')
      } catch (error) {
        console.error('Error in navigateToLoan:', error)
        this.$toast.error('Error navigating to loan case.')
      }
    }
  }
}
</script>

<style scoped>
.truncate-remarks {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.table-radius {
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}
.loan-app-link {
  transition: all 0.2s ease-in-out;
  text-decoration: none;
}
.loan-app-link:hover {
  text-decoration: underline;
  color: #1565c0 !important;
}
.loan-app-link:hover .link-icon {
  transform: translate(1px, -1px);
}
.link-icon {
  transition: transform 0.15s ease-in-out;
}
</style>
